import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, UserPlus, Copy, MessageCircle, Search } from "lucide-react";
import api, { apiError } from "../../lib/api";
import LessonFiles from "./LessonFiles";
import ReviewsAdmin from "./ReviewsAdmin";
import { toast } from "sonner";

// Admin › Course access: who has each course, how they got it, until when; give / extend / revoke access
// for any email or phone number. (3 Nov 2026)

const when = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const PERIODS = [[30, "30 days"], [90, "90 days"], [180, "6 months"], [365, "1 year"], [0, "Lifetime"]];
const SRC = { blueprint_upsell: "Bought after ebook", paid: "Bought", admin: "Given by admin", free: "Free" };
const Card = ({ children, className = "", ...p }) => <div className={`bg-white border border-[#e8e1d4] p-4 ${className}`} {...p}>{children}</div>;

function Stat({ label, value, testid }) {
    return <Card data-testid={testid}><p className="text-[11px] text-neutral-500">{label}</p><p className="text-2xl font-display font-semibold">{value}</p></Card>;
}

function badge(s) {
    if (s.revoked) return ["Removed", "bg-neutral-200 text-neutral-600"];
    if (!s.active) return ["Expired", "bg-red-100 text-red-700"];
    return [s.lifetime ? "Lifetime" : "Active", "bg-green-100 text-green-700"];
}

async function copy(text) {
    try { await navigator.clipboard.writeText(text); toast.success("Login link copied"); }
    catch { window.prompt("Copy this login link", text); }
}

export default function CourseAccess() {
    const [courses, setCourses] = useState([]);
    const [slug, setSlug] = useState("");
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [form, setForm] = useState({ email: "", phone: "", name: "", days: 365, send_email: true });
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);

    useEffect(() => {
        api.get("/admin/courses").then((r) => {
            const list = r.data || [];
            setCourses(list);
            const pref = list.find((c) => c.slug === "independent-artist-blueprint-video-course") || list[0];
            if (pref) setSlug(pref.slug);
        }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    }, []);

    const load = useCallback(() => {
        if (!slug) return;
        api.get("/admin/course-students", { params: { slug } }).then((r) => {
            setData(r.data);
            setForm((f) => ({ ...f, days: r.data.course.validity_days || 0 }));
        }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    }, [slug]);
    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => {
        const t = q.trim().toLowerCase();
        return (data?.students || []).filter((s) => {
            if (filter === "active" && !s.active) return false;
            if (filter === "expired" && (s.active || s.revoked)) return false;
            if (filter === "revoked" && !s.revoked) return false;
            return !t || [s.name, s.email, s.phone].some((v) => (v || "").toLowerCase().includes(t));
        });
    }, [data, q, filter]);

    const setValidity = async (days) => {
        try { await api.put(`/admin/courses/${slug}/validity`, { days: Number(days) }); toast.success("Access period saved for new buyers"); load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    };

    const assign = async (e) => {
        e.preventDefault();
        if (!form.email && !form.phone) { toast.error("Enter an email or a phone number"); return; }
        setBusy(true);
        try {
            const r = await api.post("/admin/course-students/assign", { slug, ...form, days: Number(form.days) });
            setLast(r.data);
            toast.success(r.data.emailed ? "Access given and login link emailed" : "Access given");
            setForm((f) => ({ ...f, email: "", phone: "", name: "" }));
            load();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        setBusy(false);
    };

    const patch = async (id, body, msg) => {
        try { await api.patch(`/admin/course-students/${id}`, body); toast.success(msg); load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    };
    const link = async (id, wa) => {
        try {
            const r = await api.post(`/admin/course-students/${id}/login-link`);
            if (wa && r.data.whatsapp_url) window.open(r.data.whatsapp_url, "_blank", "noopener");
            else copy(r.data.login_url);
        } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    };

    return (
        <div className="space-y-5 max-w-6xl" data-testid="admin-course-access">
            <div className="flex flex-wrap items-center gap-3">
                <select value={slug} onChange={(e) => setSlug(e.target.value)} className="border border-[#d9cfbc] px-3 py-2 text-sm bg-white" data-testid="ca-course">
                    {courses.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
                </select>
                <button onClick={load} className="btn-outline !py-2 !px-3 text-xs !normal-case"><RefreshCw className="h-4 w-4" /> Refresh</button>
                {data && (
                    <label className="text-xs text-neutral-600 flex items-center gap-2 ml-auto">Access period for new buyers
                        <select value={data.course.validity_days} onChange={(e) => setValidity(e.target.value)} className="border border-[#d9cfbc] px-2 py-1.5 text-xs bg-white" data-testid="ca-validity">
                            {PERIODS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                            {!PERIODS.some(([d]) => d === data.course.validity_days) && <option value={data.course.validity_days}>{data.course.validity_days} days</option>}
                        </select>
                    </label>
                )}
            </div>

            {data && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat label="Students" value={data.counts.total} testid="ca-total" />
                    <Stat label="Active" value={data.counts.active} testid="ca-active" />
                    <Stat label="Expired" value={data.counts.expired} testid="ca-expired" />
                    <Stat label="Removed" value={data.counts.revoked} testid="ca-revoked" />
                </div>
            )}

            <ReviewsAdmin slug={slug} />

            <LessonFiles course={courses.find((c) => c.slug === slug)} onDone={() => api.get("/admin/courses").then((r) => setCourses(r.data || [])).catch(() => {})} />

            <Card>
                <h4 className="font-display font-semibold text-sm mb-3 flex items-center gap-2"><UserPlus className="h-4 w-4" /> Give access</h4>
                <form onSubmit={assign} className="grid sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_auto_auto] gap-3 items-end">
                    <label className="text-xs text-neutral-600">Email
                        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="artist@gmail.com" className="mt-1 w-full border border-[#d9cfbc] px-3 py-2 text-sm" data-testid="ca-email" /></label>
                    <label className="text-xs text-neutral-600">Phone (WhatsApp)
                        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="98XXXXXXXX" className="mt-1 w-full border border-[#d9cfbc] px-3 py-2 text-sm" data-testid="ca-phone" /></label>
                    <label className="text-xs text-neutral-600">Name
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Optional" className="mt-1 w-full border border-[#d9cfbc] px-3 py-2 text-sm" /></label>
                    <label className="text-xs text-neutral-600">For
                        <select value={form.days} onChange={(e) => setForm({ ...form, days: Number(e.target.value) })} className="mt-1 w-full border border-[#d9cfbc] px-3 py-2 text-sm bg-white" data-testid="ca-days">
                            {PERIODS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
                        </select></label>
                    <button disabled={busy} className="btn-dark !py-2.5 !px-5 text-xs" data-testid="ca-assign">{busy ? "Saving…" : "Give access"}</button>
                </form>
                <label className="mt-3 flex items-center gap-2 text-xs text-neutral-600">
                    <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} /> Email them a login link (email only; for a phone number, send the link on WhatsApp below)
                </label>
                {last && (
                    <div className="mt-3 bg-cream border border-[#e0d6c2] px-3 py-2.5 text-xs flex flex-wrap items-center gap-3" data-testid="ca-last">
                        <span>Access given{last.lifetime ? " for lifetime" : ` until ${when(last.expires_at)}`}.</span>
                        <button onClick={() => copy(last.login_url)} className="underline inline-flex items-center gap-1"><Copy className="h-3.5 w-3.5" /> Copy login link</button>
                        {last.whatsapp_url && <a href={last.whatsapp_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> Send on WhatsApp</a>}
                    </div>
                )}
            </Card>

            <Card>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-neutral-400" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone" className="border border-[#d9cfbc] pl-8 pr-3 py-2 text-sm w-64" data-testid="ca-search" /></div>
                    {[["all", "All"], ["active", "Active"], ["expired", "Expired"], ["revoked", "Removed"]].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs border ${filter === k ? "bg-espresso text-white border-espresso" : "border-[#d9cfbc]"}`}>{l}</button>
                    ))}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="ca-table">
                        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-neutral-500 border-b border-[#eee6d8]">
                            <th className="py-2 pr-3">Student</th><th className="py-2 pr-3">How</th><th className="py-2 pr-3">Since</th><th className="py-2 pr-3">Access</th><th className="py-2 pr-3">Progress</th><th className="py-2">Manage</th>
                        </tr></thead>
                        <tbody>
                            {rows.map((s) => {
                                const [bl, bc] = badge(s);
                                return (
                                    <tr key={s.id} className="border-b border-[#f1ebe0] align-top">
                                        <td className="py-2.5 pr-3"><p className="font-semibold">{s.name || "—"}</p><p className="text-xs text-neutral-500">{s.email || ""}{s.email && s.phone ? " · " : ""}{s.phone ? `+${s.phone}` : ""}</p></td>
                                        <td className="py-2.5 pr-3 text-xs">{SRC[s.source] || s.source}{s.amount ? ` · ₹${s.amount}` : ""}</td>
                                        <td className="py-2.5 pr-3 text-xs">{when(s.created_at)}</td>
                                        <td className="py-2.5 pr-3 text-xs"><span className={`px-2 py-0.5 ${bc}`}>{bl}</span>{!s.lifetime && s.expires_at && <p className="mt-1 text-neutral-500">{s.active ? "until" : "ended"} {when(s.expires_at)}</p>}</td>
                                        <td className="py-2.5 pr-3 text-xs">{s.progress || 0}%</td>
                                        <td className="py-2.5 text-xs">
                                            <div className="flex flex-wrap gap-1.5">
                                                {!s.lifetime && <select defaultValue="" onChange={(e) => { const v = Number(e.target.value); e.target.value = ""; if (v === 0) patch(s.id, { action: "lifetime" }, "Set to lifetime"); else if (v) patch(s.id, { action: "extend", days: v }, `Extended by ${v} days`); }} className="border border-[#d9cfbc] px-1.5 py-1 bg-white" data-testid={`ca-extend-${s.id}`}>
                                                    <option value="">Extend…</option><option value="30">+30 days</option><option value="90">+90 days</option><option value="365">+1 year</option><option value="0">Lifetime</option>
                                                </select>}
                                                {s.revoked
                                                    ? <button onClick={() => patch(s.id, { action: "restore" }, "Access restored")} className="border border-[#d9cfbc] px-2 py-1">Restore</button>
                                                    : <button onClick={() => { if (window.confirm(`Remove ${s.name || s.email || "this student"}'s access?`)) patch(s.id, { action: "revoke" }, "Access removed"); }} className="border border-red-200 text-red-700 px-2 py-1" data-testid={`ca-revoke-${s.id}`}>Remove</button>}
                                                <button onClick={() => link(s.id, false)} className="border border-[#d9cfbc] px-2 py-1 inline-flex items-center gap-1" title="Copy a one-time login link"><Copy className="h-3 w-3" /> Link</button>
                                                {s.phone && <button onClick={() => link(s.id, true)} className="border border-[#d9cfbc] px-2 py-1 inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" /> WhatsApp</button>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {!rows.length && <tr><td colSpan={6} className="py-8 text-center text-neutral-400 text-sm">No students yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
